/* TheraPaw shared motion | v1.0
 * No dependencies. No click interception, cart requests or DOM reparenting.
 * Controls: theme.liquid's data-reveal-enabled and data-button-bounce flags.
 * Fine tuning: stage delays below; timing/distance in therapaw-global.css.
 */
(() => {
  'use strict';
  if (window.TheraPawMotion) return;

  const script = document.currentScript;
  const config = {
    reveal: script?.dataset.revealEnabled !== 'false',
    bounce: script?.dataset.buttonBounce !== 'false',
    stageDelay: [0, 90, 180, 250], // Heading -> copy -> visual -> follow-up.
  };
  const html = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify?.designMode) || html.classList.contains('shopify-design-mode');
  const hasObserver = 'IntersectionObserver' in window;
  const pending = new Set();
  const registered = new WeakSet();
  const buttonSet = new Set();
  const rootsToScan = new Set();
  let frame = 0;
  let scrollFrame = 0;
  let mutationObserver;

  html.dataset.tpButtonBounce = String(config.bounce);
  if (designMode) html.setAttribute('data-tp-design-mode', '');

  const CTA_SELECTOR = [
    'a.button', 'button.button', 'input.button',
    '.tp-cta', '.tph__cta', '.product-form__submit', '.cart__checkout-button',
    '#CartDrawer-Checkout', '#CartDrawer-ContinueShopping',
    '.customer button[type="submit"]', '.contact button[type="submit"]',
    '.newsletter-form button[type="submit"]',
    'a[class^="pawcalm-"][class$="__button"]',
    'button[class^="pawcalm-"][class$="__button"]',
    'a[class^="pawcalm-"][class$="__cta"]',
    'button[class^="pawcalm-"][class$="__cta"]',
    'a[class^="pdp-guarantee-"][class$="__button"]',
    'button[class^="pdp-guarantee-"][class$="__button"]',
    '[data-tp-cta]',
  ].join(',');
  const UTILITY_SELECTOR = [
    '.skip-to-content-link', '.quantity__button', '.tp-gallery-control', '.tp-thumb',
    '.slider-button', '.slideshow__autoplay', '.modal__close-button', '.drawer__close',
    '.search__button', '.reset__button', '.header__icon', '.facets__reset',
    '[data-story-next]', '[data-testi-prev]', '[data-testi-next]', '[data-next]', '[data-prev]',
    '[role="tab"]', '[role="switch"]', '.menu-drawer__close-button',
    '[data-tp-no-bounce]',
  ].join(',');
  const NO_REVEAL = [
    '[data-therapaw-pdp]', '[data-tp-hero]', 'cart-drawer', '.drawer',
    '.tp-sticky', '.header-wrapper', '[role="dialog"]', 'dialog',
    '[class^="scroll-ticker-"]', '[data-tp-no-reveal]',
  ].join(',');

  function includingRoot(root, selector) {
    return [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  }
  function show(element, instant = false) {
    if (!element.isConnected) {
      pending.delete(element);
      revealObserver?.unobserve(element);
      return;
    }
    if (instant) {
      element.style.setProperty('--tp-reveal-delay', '0ms');
      element.style.setProperty('transition', 'none', 'important');
    }
    element.dataset.tpReveal = 'shown';
    pending.delete(element);
    revealObserver?.unobserve(element);
  }
  function showAll() {
    [...pending].forEach(element => show(element, true));
  }

  const revealObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      // threshold 0 works even for a comparison/table taller than the viewport.
      if (entry.isIntersecting) show(entry.target);
    });
  }, { rootMargin: '0px 0px -32px 0px', threshold: 0 }) : null;

  const buttonObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.toggleAttribute('data-tp-button-visible', entry.isIntersecting));
  }, { threshold: 0 }) : null;

  function markButtons(root) {
    includingRoot(root, CTA_SELECTOR).forEach(button => {
      if (button.matches(UTILITY_SELECTOR)) return;
      // Icon-only buttons must not continuously move. Explicit opt-in is allowed.
      const label = button.tagName === 'INPUT' ? button.value : button.textContent.trim();
      if (!label && !button.hasAttribute('data-tp-cta')) return;
      button.setAttribute('data-tp-cta', '');
      if (buttonSet.has(button)) return;
      buttonSet.add(button);
      if (buttonObserver) buttonObserver.observe(button);
      else button.setAttribute('data-tp-button-visible', '');
    });
  }

  function arm(element, stage) {
    if (!element || registered.has(element) || !element.isConnected) return;
    registered.add(element);
    if (!config.reveal || reducedMotion.matches || designMode || !hasObserver) return;
    if (element.closest(NO_REVEAL) || element.closest('[hidden], [aria-hidden="true"]')) return;
    // Do not hide disclosure answers or descendants of already animated groups.
    if (element.closest('details:not([open])') || element.parentElement?.closest('[data-tp-reveal]')) return;
    const bounds = element.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    // First viewport, back/forward scroll restoration and content already passed
    // all remain visible. Below-the-fold content is progressively enhanced.
    if (bounds.top < window.innerHeight + 16) return;
    if (['fixed', 'sticky'].includes(getComputedStyle(element).position)) return;
    element.style.setProperty('--tp-reveal-delay', `${config.stageDelay[Math.min(stage, 3)]}ms`);
    element.dataset.tpReveal = 'pending';
    pending.add(element);
    revealObserver.observe(element);
  }

  function stage(root, selector, level) {
    root.querySelectorAll(selector).forEach(element => arm(element, level));
  }
  function sectionMotion(section) {
    if (section.matches(NO_REVEAL) || section.closest(NO_REVEAL)) return;
    const cls = section.className || '';
    // Individual selectors target the actual exported sections. Carousels and
    // tables are revealed as a group, never one hidden horizontal slide at a time.
    if (section.matches('[data-tp-faq]')) {
      stage(section, '.tpf__eyebrow, .tpf__heading', 0);
      stage(section, '.tpf__subheading', 1);
      stage(section, '.tpf__list', 2);
      stage(section, '.tpf__support', 3);
    } else if (cls.startsWith('pawcalm-story-')) {
      stage(section, '[class$="__eyebrow"], [class$="__heading"]', 0);
      stage(section, '[class$="__subheading"]', 1);
      stage(section, '[class$="__track"]', 2);
      stage(section, '[class$="__banner"]', 3);
    } else if (cls.startsWith('pawcalm-compare-')) {
      stage(section, '[class$="__heading"]', 0);
      stage(section, '[class$="__subheading"]', 1);
      stage(section, '[class$="__table-wrap"]', 2);
      stage(section, '[class$="__caption"]', 3);
    } else if (cls.startsWith('pawcalm-benefits-grid-')) {
      stage(section, '[class$="__heading"]', 0);
      stage(section, '[class$="__grid"]', 1);
      stage(section, '[class$="__image-wrap"]', 2);
    } else if (cls.startsWith('pawcalm-testi-')) {
      stage(section, '[class$="__eyebrow"], [class$="__heading"]', 0);
      stage(section, '[class$="__subheading"]', 1);
      stage(section, '[class$="__carousel"]', 2);
      stage(section, '[class$="__cta-row"]', 3);
    } else if (cls.startsWith('pawcalm-howto-')) {
      stage(section, '[class$="__eyebrow"], [class$="__heading"]', 0);
      stage(section, '[class$="__intro"]', 1);
      stage(section, '[class$="__video-frame"], [class$="__video-placeholder"]', 2);
      stage(section, '[class$="__step"], [class$="__caption"], [class$="__callout"]', 3);
    } else if (cls.startsWith('reasons-band-')) {
      stage(section, '[class$="__heading"]', 0);
      stage(section, '[class$="__grid"]', 1);
      stage(section, '[class*="__bookend"]', 2);
    } else if (cls.startsWith('pdp-guarantee-') || cls.startsWith('pawcalm-offer-card-')) {
      stage(section, '[class$="__badge"], [class$="__heading"]', 0);
      stage(section, '[class$="__text"], [class$="__subheading"], [class$="__description"]', 1);
      stage(section, '[class$="__seal-area"], [class$="__visual"]', 2);
      // The purchase action itself always stays visible and clickable.
    } else {
      // Native theme sections: no individual product cards or interactive media.
      stage(section, ':scope > h1, :scope > h2, .title-wrapper, .title-wrapper-with-link, .rich-text__heading, .image-with-text__heading, .collapsible-content__heading', 0);
      stage(section, '.rich-text__text, .image-with-text__text:not(.image-with-text__text--caption), .collection__description', 1);
      stage(section, '.image-with-text__media', 2);
    }
  }

  function scan(root = document) {
    markButtons(root);
    const sections = includingRoot(root, '#MainContent > .shopify-section, #MainContent section, #MainContent [data-tp-faq]');
    sections.forEach(sectionMotion);
  }
  function cleanRemovedElements() {
    [...pending].forEach(element => {
      if (!element.isConnected) {
        pending.delete(element);
        revealObserver?.unobserve(element);
      }
    });
    [...buttonSet].forEach(button => {
      if (!button.isConnected) {
        buttonSet.delete(button);
        buttonObserver?.unobserve(button);
      }
    });
  }
  function queueScan(root) {
    if (root?.nodeType !== 1) return;
    rootsToScan.add(root);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      cleanRemovedElements();
      rootsToScan.forEach(node => { if (node.isConnected) scan(node); });
      rootsToScan.clear();
    });
  }
  function revealAroundTarget(target) {
    if (!(target instanceof Element)) return;
    pending.forEach(element => {
      if (element === target || element.contains(target) || target.contains(element)) show(element, true);
    });
  }
  function revealHash() {
    if (!location.hash) return;
    try { revealAroundTarget(document.getElementById(decodeURIComponent(location.hash.slice(1)))); } catch (_) { /* Invalid fragments do not affect content. */ }
  }

  function start() {
    try {
      scan(document);
      revealHash();
      mutationObserver = new MutationObserver(records => {
        records.forEach(record => {
          record.addedNodes.forEach(queueScan);
          // Handles sections removed by the theme editor as well as cart refreshes.
          if (record.removedNodes.length) queueScan(record.target);
        });
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      // A selector/theme error must never leave sales content hidden.
      showAll();
      console.warn('TheraPaw motion: content remains visible.', error);
    }
  }

  // Focus and skip/anchor links must never land on invisible content.
  document.addEventListener('focusin', event => revealAroundTarget(event.target));
  window.addEventListener('hashchange', revealHash);
  window.addEventListener('pageshow', event => { if (event.persisted) showAll(); revealHash(); });
  document.addEventListener('shopify:section:load', event => queueScan(event.target));
  document.addEventListener('shopify:section:select', event => revealAroundTarget(event.target));
  document.addEventListener('shopify:block:select', event => revealAroundTarget(event.target));

  // A fast scroll may skip an observer's intersection completely. Reveal passed
  // content immediately rather than leaving a blank block when scrolling back.
  window.addEventListener('scroll', () => {
    if (!pending.size || scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      pending.forEach(element => {
        if (element.getBoundingClientRect().bottom < 40) show(element, true);
      });
    });
  }, { passive: true });
  const preferenceChange = () => { if (reducedMotion.matches) showAll(); };
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', preferenceChange);
  else reducedMotion.addListener(preferenceChange);

  window.TheraPawMotion = { refresh: () => scan(document), revealAll: showAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
